import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { collection, getDocs } from "firebase/firestore";

import Navbar from "../Navbar";
import { db } from "../firebaseConfig";
import { DestImgData } from "../Constants";

const packageTypes = [
  { id: "all", label: "All" },
  { id: "domestic", label: "Domestic" },
  { id: "international", label: "International" },
];

const destinations = [
  { id: "all", label: "All Destinations" },
  { id: "himachal", label: "Himachal Pradesh" },
  { id: "kerala", label: "Kerala" },
  { id: "rajasthan", label: "Rajasthan" },
  { id: "kashmir", label: "Kashmir" },
  { id: "goa", label: "Goa" },
  { id: "thailand", label: "Thailand" },
  { id: "bali", label: "Bali" },
  { id: "dubai", label: "Dubai" },
  { id: "singapore", label: "Singapore" },
  { id: "maldives", label: "Maldives" },
];

const durations = [
  { id: "all", label: "Any Duration" },
  { id: "1-3", label: "1-3 Days" },
  { id: "4-6", label: "4-6 Days" },
  { id: "7+", label: "7+ Days" },
];

// const ExpandMore = styled(IconButton)(({ theme }) => ({

const ExpandMore = styled(IconButton)(({ theme }) => ({
  transform: "rotate(0deg)",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
}));

ExpandMore.defaultProps = {
  expand: false,
};

const PackageCard = ({ pkg }) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const currencySymbol = pkg.currency === "INR" ? "₹" : `${pkg.currency} `;
  const destImgItem = DestImgData[pkg.destination.toLowerCase()];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Card sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, overflow: "hidden", height: { xs: "auto", md: 170 }, minHeight: { xs: 180, md: 170 } }}>
        {/* Image - Left Side */}
        <CardMedia
          component="img"
          sx={{ width: { xs: "100%", md: 200 }, height: { xs: 120, md: "auto" }, objectFit: "cover" }}
          image={ destImgItem?.img || destImgItem?.imgList[0] }
          alt={pkg.destination}
        />

        {/* Main Content - Middle */}
        <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-start", minHeight: 140 }}>
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, flexWrap: "nowrap", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, overflow: "hidden" }}>
                <Typography variant="h6" sx={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {pkg.title}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary", whiteSpace: "nowrap" }}>
                  {pkg.duration}
                </Typography>
              </Box>
              <Chip
                label={pkg.type === "domestic" ? "Domestic" : "International"}
                color={pkg.type === "domestic" ? "success" : "primary"}
                size="small"
                sx={{ height: 20, fontSize: "0.65rem" }}
              />
            </Box>
            {/* Route Display */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                mb: 0.5,
                fontWeight: 500
              }}
            >
              Route: {pkg.route}
            </Typography>

            {/* Cab Pricing */}
            <Box sx={{ mt: 0 }}>
              <Stack direction="row" spacing={1} sx={{ overflowX: "auto", pb: 1, "&::-webkit-scrollbar": { display: "none" }, msOverflowStyle: "none", scrollbarWidth: "none" }}>
                {pkg.cabPricing && pkg.cabPricing.map((cab, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      minWidth: 90,
                      maxWidth: 110,
                      p: 0.8,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                      "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
                      transition: "all 0.2s",
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.7rem" }}>
                      {cab.cabType}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.6rem", mb: 0.2 }}>
                      {cab.seats} Seater
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "primary.main", lineHeight: 1, fontSize: "0.85rem" }}>
                      {currencySymbol}{cab.price?.toLocaleString()}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Box>
        </CardContent>

        {/* Buttons - Right Side */}
        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", p: 2, minWidth: 120, gap: 1, borderLeft: { md: "1px solid rgba(0,0,0,0.12)" } }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate(`/packages/${pkg.id}`)}
          >
            View Details
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={() => setExpanded(!expanded)}
            endIcon={<ExpandMoreIcon sx={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s" }} />}
          >
            {expanded ? "Less" : "Quick View"}
          </Button>
        </Box>
      </Card>

      {/* Expanded View - Outside Card */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Card sx={{ display: "flex", flexDirection: "column", overflow: "hidden", mt: -1, mb: 3 }}>
          <CardContent sx={{ flex: 1, width: "100%" }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Quick Details
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Route:
              </Typography>
              <Typography variant="caption" display="block">
                {pkg.route}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Itinerary:
              </Typography>
              <Typography variant="caption" display="block" sx={{ whiteSpace: "pre-line" }}>
                {pkg.itinerary}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Policy:
              </Typography>
              <Typography variant="caption" display="block">
                {pkg.policies || "Standard terms apply."}
              </Typography>
            </Box>
          </CardContent>

          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setExpanded(false)}
              startIcon={<ExpandMoreIcon sx={{ transform: "rotate(180deg)" }} />}
            >
              Close
            </Button>
          </Box>
        </Card>
      </Collapse>
    </Box>
  );
};

const Packages = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [packageType, setPackageType] = useState("all");
  const [destination, setDestination] = useState("all");
  const [duration, setDuration] = useState("all");

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "transportPackages"));
        const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPackages(data);
      } catch (error) {
        console.error("Error fetching packages:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPackages();
  }, []);

  const handlePackageTypeChange = (event, newType) => {
    if (newType !== null) setPackageType(newType);
  };

  const filteredPackages = packages.filter((pkg) => {
    if (packageType !== "all" && pkg.type !== packageType) return false;
    if (destination !== "all" && pkg.destination?.toLowerCase() !== destination.toLowerCase()) return false;
    if (duration !== "all") {
      const days = parseInt(pkg.duration);
      if (duration === "1-3" && (isNaN(days) || days < 1 || days > 3)) return false;
      if (duration === "4-6" && (isNaN(days) || days < 4 || days > 6)) return false;
      if (duration === "7+" && (isNaN(days) || days < 7)) return false;
    }
    return true;
  });

  const isDestinationMissing = destination !== "all" &&
    !destinations.some(d => d.label.toLowerCase() === destination.toLowerCase());

  if (loading) {
    return (
      <>
        <Navbar />
        <Box sx={{ pt: 15, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Box sx={{ pt: 2, pb: 5, px: 2,  mx: "auto" }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
          Transport Packages
        </Typography>

        {/* Filters */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            mb: 4,
            alignItems: "center",
          }}
        >
          <ToggleButtonGroup
            value={packageType}
            exclusive
            onChange={handlePackageTypeChange}
            aria-label="package type"
            sx={{ flexWrap: "wrap" }}
          >
            {packageTypes.map((type) => (
              <ToggleButton key={type.id} value={type.id} aria-label={type.label}>
                {type.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Autocomplete
            options={destinations.filter(d => d.id !== 'all')}
            getOptionLabel={(option) => option.label}
            value={destinations.find(d => d.label.toLowerCase() === destination.toLowerCase()) || null}
            onChange={(event, newValue) => {
              setDestination(newValue ? newValue.label : "all");
            }}
            onInputChange={(event, newInputValue) => {
              setDestination(newInputValue || "all");
            }}
            sx={{ minWidth: 250 }}
            renderInput={(params) => (
              <TextField {...params} label="Destination" size="small" />
            )}
            freeSolo
          />

          <FormControl sx={{ minWidth: 140 }} size="small">
            <InputLabel>Duration</InputLabel>
            <Select
              value={duration}
              label="Duration"
              onChange={(e) => setDuration(e.target.value)}
            >
              {durations.map((dur) => (
                <MenuItem key={dur.id} value={dur.id}>
                  {dur.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Package Cards */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {filteredPackages.map((pkg) => (
            <PackageCard key={pkg.id} pkg={pkg} />
          ))}
        </Box>

        {filteredPackages.length === 0 && (
          <Box sx={{ textAlign: "center", py: 8 }}>
            {isDestinationMissing ? (
              <Box sx={{ p: 4, bgcolor: "#fff", borderRadius: 4, boxShadow: "0 4px 20px rgba(0,0,0,0.05)", display: "inline-block", maxWidth: 500 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Can't find your destination?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  We can help you organize a custom transport package for {destination}. Share your requirements and we'll get back to you with the best options.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate(`/post-req/${encodeURIComponent(destination)}`)}
                  sx={{ px: 4, borderRadius: 2, fontWeight: 600 }}
                >
                  Share Request Details
                </Button>
              </Box>
            ) : (
              <Typography
                variant="body1"
                sx={{ color: "text.secondary" }}
              >
                No packages found matching your filters.
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

export default Packages;
